import React from 'react';
import type { ConversionStats, ConversionStatus, SystemStatsComplete } from '../../domain/entities/Analytics';
import type { FilteredStats } from '../../data/services/ReportsService';

interface ConversionPanelProps {
  systemStats: SystemStatsComplete | null;
  filteredStats: FilteredStats | null;
  isFiltered: boolean;
  period: string;
  hasCameraFilter: boolean;
  isUpdating?: boolean;
}

const statusMessages: Partial<Record<ConversionStatus, string>> = {
  no_checkout_data: 'Sem registros de checkout neste período. Isso não significa que não houve compras.',
  no_entrance_people: 'Sem entradas contabilizadas no período para calcular a taxa.',
  configuration_conflict: 'Revise as câmeras: checkout não deve ser também entrada ou saída.',
  inconsistent_counts: 'O checkout superou as entradas. Revise a cobertura das câmeras e possíveis recontagens.',
};

/** Period data is authoritative; never mix historic totals with today's rate. */
export const ConversionPanel: React.FC<ConversionPanelProps> = ({
  systemStats, filteredStats, isFiltered, period, hasCameraFilter, isUpdating = false,
}) => {
  if (hasCameraFilter) return null;
  let conversion: ConversionStats | null | undefined;
  if (isFiltered) {
    conversion = filteredStats?.conversion;
    // Compatibility with older API/cache entries: missing is not measured zero.
    if (!conversion && systemStats?.has_conversion_cameras && filteredStats) {
      conversion = {
        has_cameras: true,
        cameras_count: systemStats.conversion_cameras_count ?? 0,
        checkout_people: 0,
        entrance_people: filteredStats.summary.total_people,
        rate: null,
        status: 'no_checkout_data',
      };
    }
  } else if (systemStats?.has_conversion_cameras) {
    conversion = {
      has_cameras: true,
      cameras_count: systemStats.conversion_cameras_count ?? 0,
      active_cameras_count: systemStats.conversion_cameras_count,
      has_entrance_cameras: systemStats.today_conversion_has_entrance_cameras,
      checkout_people: systemStats.today_conversion_people ?? 0,
      checkout_detections: systemStats.today_conversion_detections,
      entrance_people: systemStats.today_people,
      rate: systemStats.today_conversion_rate ?? null,
      status: systemStats.today_conversion_status,
    };
  }
  if (!conversion?.has_cameras) return null;

  const status = conversion.has_entrance_cameras === false ? 'no_entrance_people'
    : conversion.status ?? (conversion.entrance_people <= 0
    ? 'no_entrance_people'
    : conversion.checkout_people > conversion.entrance_people
      ? 'inconsistent_counts' : 'available');
  const validRate = (status === 'available' || status === 'inconsistent_counts')
    && conversion.rate !== null && Number.isFinite(conversion.rate);
  const comparable = status === 'available' && validRate;
  const checkoutMissing = status === 'no_checkout_data' || conversion.checkout_detections === 0;
  const checkout = checkoutMissing ? '—' : conversion.checkout_people.toLocaleString('pt-BR');
  const entrance = conversion.entrance_people.toLocaleString('pt-BR');
  const periodLabel = period === 'today' ? 'Hoje' : period === 'week' ? '7 Dias'
    : period === 'month' ? '30 Dias' : 'Período';
  const message = conversion.has_entrance_cameras === false
    ? 'Sem câmeras de entrada configuradas. Configure uma entrada para calcular a conversão do checkout.'
    : statusMessages[status];

  return (
    <section aria-label="Conversão por fluxo" className="mb-4 sm:mb-6">
      {isUpdating && <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
        Atualizando o dashboard. Valores da última consulta concluída.
      </p>}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 md:gap-4">
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 p-3 sm:p-4 md:p-6 rounded-lg shadow-md border border-amber-200 dark:border-amber-800 flex flex-col h-full">
          <h2 className="text-[10px] sm:text-xs md:text-sm text-gray-700 dark:text-gray-300 font-semibold mb-2">
            🛒 Taxa de Conversão ({periodLabel})
          </h2>
          <p className="text-2xl sm:text-3xl md:text-4xl font-bold text-amber-600 dark:text-amber-400 my-auto">
            {validRate ? `${conversion.rate!.toFixed(1)}%` : '—'}
          </p>
          <p className="text-[9px] sm:text-xs text-gray-500 dark:text-gray-400 mt-2">
            {checkout} no caixa / {entrance} na entrada
          </p>
        </div>
        <div className="bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 p-3 sm:p-4 md:p-6 rounded-lg shadow-md border border-red-200 dark:border-red-800 flex flex-col h-full">
          <h2 className="text-[10px] sm:text-xs md:text-sm text-gray-700 dark:text-gray-300 font-semibold mb-2">
            🚶 Não Convertidos ({periodLabel})
          </h2>
          <p className="text-2xl sm:text-3xl md:text-4xl font-bold text-red-600 dark:text-red-400 my-auto">
            {comparable ? (conversion.entrance_people - conversion.checkout_people).toLocaleString('pt-BR') : '—'}
          </p>
          <p className="text-[9px] sm:text-xs text-gray-500 dark:text-gray-400 mt-2">
            {comparable ? `${entrance} entraram — ${checkout} no caixa` : 'Comparação indisponível para este período'}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 p-3 sm:p-4 md:p-6 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 flex flex-col h-full">
          <h2 className="text-[10px] sm:text-xs md:text-sm text-gray-600 dark:text-gray-400 mb-2">Câmeras de Checkout</h2>
          <p className="text-2xl sm:text-3xl md:text-4xl font-bold text-amber-600 dark:text-amber-400 my-auto">
            {conversion.cameras_count}
          </p>
          <p className="text-[9px] sm:text-xs text-gray-500 dark:text-gray-400 mt-2">
            configuradas para o cálculo
            {conversion.active_cameras_count !== undefined && ` · ${conversion.active_cameras_count} habilitadas atualmente`}
          </p>
        </div>
      </div>
      {message && <p role="status" className="mt-2 text-xs text-amber-800 dark:text-amber-300">{message}</p>}
      <p className="mt-2 text-[10px] sm:text-xs text-gray-500 dark:text-gray-400">
        Estimativa por fluxo: pessoas contabilizadas no caixa ÷ entradas. Não confirma compras
        nem identifica visitantes únicos. “Não convertidos” é a diferença estimada dessas contagens.
      </p>
    </section>
  );
};
